import { useState, Fragment, useEffect } from 'react'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Organization,
  Platform,
  Reseller,
  OrgDeletionImpact,
  listOrganizations,
  createOrganization,
  listPlatforms,
  listPlatformResellers,
  updateOrganizationPlatformLink,
  getOrgDeletionImpact,
  deleteOrganization,
} from '../api/client'
import { useAuth } from '../auth/AuthContext'
import SearchableSelect from '../components/SearchableSelect'

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 20,
  marginBottom: 16,
}

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' }
const thStyle: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
}
const tdStyle: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 14 }
const btnStyle: React.CSSProperties = { padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }
const inputSt: React.CSSProperties = { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }

export default function Organizations() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { data: orgs = [] } = useQuery<Organization[]>({ queryKey: ['organizations'], queryFn: listOrganizations })
  const { data: platforms = [] } = useQuery<Platform[]>({ queryKey: ['platforms'], queryFn: listPlatforms })

  const [newName, setNewName] = useState('')
  // 'own' = nest under my own org (server default), 'top' = no parent at
  // all (super users only — see createOrganization), or a specific org ID.
  const [newParentID, setNewParentID] = useState<number | 'own' | 'top'>('own')
  const [expandedOrg, setExpandedOrg] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [showBulkDelete, setShowBulkDelete] = useState(false)
  const [search, setSearch] = useState('')

  const orgName = (id: number | null) => (id == null ? '—' : orgs.find(o => o.ID === id)?.Name ?? `#${id}`)

  const searchLower = search.trim().toLowerCase()
  const filteredOrgs = orgs.filter(o => {
    if (!searchLower) return true
    return o.Name.toLowerCase().includes(searchLower) || orgName(o.ParentID).toLowerCase().includes(searchLower)
  })

  // Only orgs other than the caller's own can ever be selected/deleted —
  // matches the per-row Delete button's restriction. Scoped to the
  // currently-filtered rows so "select all" doesn't reach outside search.
  const selectableOrgs = filteredOrgs.filter(o => o.ID !== user?.OrgID)
  const toggleSelected = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const allSelected = selectableOrgs.length > 0 && selected.size === selectableOrgs.length
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(selectableOrgs.map(o => o.ID)))

  const createMut = useMutation({
    mutationFn: () => {
      if (newParentID === 'top') return createOrganization(null, newName, true)
      if (newParentID === 'own') return createOrganization(null, newName, false)
      return createOrganization(newParentID, newName, false)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organizations'] })
      setNewName('')
      setNewParentID('own')
    },
  })

  const canManage = user?.IsSuperUser || user?.CanManageUsers

  return (
    <div>
      <h3 style={{ margin: '0 0 16px' }}>Organizations</h3>

      {canManage && (
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 12px' }}>New Organization</h4>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Name</label>
              <input style={inputSt} value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. MFE Reseller" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Parent</label>
              <select
                style={inputSt}
                value={newParentID}
                onChange={e => setNewParentID(e.target.value === 'own' || e.target.value === 'top' ? e.target.value : Number(e.target.value))}
              >
                <option value="own">— your org —</option>
                {user?.IsSuperUser && <option value="top">— top level, no parent —</option>}
                {orgs.map(o => <option key={o.ID} value={o.ID}>{o.Name}</option>)}
              </select>
            </div>
            <button
              style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
              onClick={() => createMut.mutate()}
              disabled={!newName.trim() || createMut.isPending}
            >
              {createMut.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input
          style={{ flex: 1, maxWidth: 320, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          placeholder="Search by name or parent…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            style={{ ...btnStyle, background: 'none', border: 'none', color: '#6b7280' }}
            onClick={() => setSearch('')}
          >
            Clear filter
          </button>
        )}
      </div>

      {user?.IsSuperUser && selected.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>
            {selected.size} organization{selected.size !== 1 ? 's' : ''} selected
          </span>
          <button
            style={{ ...btnStyle, background: '#dc2626', color: '#fff' }}
            onClick={() => setShowBulkDelete(true)}
          >
            Delete Selected
          </button>
          <button
            style={{ ...btnStyle, background: 'none', border: 'none', color: '#6b7280' }}
            onClick={() => setSelected(new Set())}
          >
            Clear selection
          </button>
        </div>
      )}

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {user?.IsSuperUser && (
                <th style={{ ...thStyle, width: 36 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
              )}
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Parent</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Platform</th>
              {user?.IsSuperUser && <th style={thStyle}></th>}
            </tr>
          </thead>
          <tbody>
            {filteredOrgs.length === 0 && (
              <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>
                {orgs.length === 0 ? 'No organizations visible.' : 'No organizations match your search.'}
              </td></tr>
            )}
            {filteredOrgs.map(org => (
              <Fragment key={org.ID}>
                <tr>
                  {user?.IsSuperUser && (
                    <td style={tdStyle}>
                      {org.ID !== user.OrgID && (
                        <input type="checkbox" checked={selected.has(org.ID)} onChange={() => toggleSelected(org.ID)} />
                      )}
                    </td>
                  )}
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{org.Name}</td>
                  <td style={tdStyle}>{orgName(org.ParentID)}</td>
                  <td style={tdStyle}>{new Date(org.CreatedAt).toLocaleDateString()}</td>
                  <td style={tdStyle}>
                    <button
                      style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
                      onClick={() => setExpandedOrg(expandedOrg === org.ID ? null : org.ID)}
                    >
                      {expandedOrg === org.ID ? 'Hide' : 'Manage'}
                    </button>
                  </td>
                  {user?.IsSuperUser && (
                    <td style={tdStyle}>
                      {org.ID !== user.OrgID && (
                        <button style={{ ...btnStyle, background: '#fee2e2', color: '#991b1b' }} onClick={() => setDeleteTarget(org)}>
                          Delete
                        </button>
                      )}
                    </td>
                  )}
                </tr>
                {expandedOrg === org.ID && (
                  <tr>
                    <td colSpan={6} style={{ ...tdStyle, background: '#f9fafb' }}>
                      <PlatformLinkPanel org={org} platforms={platforms} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {deleteTarget && (
        <DeleteOrgModal
          org={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); qc.invalidateQueries({ queryKey: ['organizations'] }) }}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteOrgModal
          orgs={orgs}
          selectedIDs={Array.from(selected)}
          onClose={() => setShowBulkDelete(false)}
          onDeleted={() => {
            setShowBulkDelete(false)
            setSelected(new Set())
            qc.invalidateQueries({ queryKey: ['organizations'] })
          }}
        />
      )}
    </div>
  )
}

// DeleteOrgModal fetches a preview of what deleting org's whole subtree
// would take with it (see server's GetOrgDeletionImpact) before letting the
// operator confirm — deleting cascades to every user/platform/enrollment
// code under any org in the subtree, so this needs to be an informed
// decision, not a blind confirm dialog.
function DeleteOrgModal({ org, onClose, onDeleted }: { org: Organization; onClose: () => void; onDeleted: () => void }) {
  const { data: impact, isLoading, error: loadError } = useQuery<OrgDeletionImpact>({
    queryKey: ['org-deletion-impact', org.ID],
    queryFn: () => getOrgDeletionImpact(org.ID),
  })
  const [confirmText, setConfirmText] = useState('')
  const [error, setError] = useState<string | null>(null)

  const deleteMut = useMutation({
    mutationFn: () => deleteOrganization(org.ID),
    onSuccess: onDeleted,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
  })

  const canConfirm = !!impact && confirmText.trim() === org.Name

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>Delete "{org.Name}"</h2>

        {isLoading && <p style={{ fontSize: 13, color: '#6b7280' }}>Checking what this would affect…</p>}
        {loadError && <p style={{ color: '#dc2626', fontSize: 13 }}>Could not load deletion impact.</p>}

        {impact && (
          <>
            <p style={{ fontSize: 13, color: '#374151', margin: '0 0 12px' }}>
              This deletes {impact.OrgIDs.length === 1 ? 'this organization' : `this organization and ${impact.OrgIDs.length - 1} descendant org(s)`}:
            </p>
            <ul style={{ fontSize: 13, color: '#374151', margin: '0 0 12px', paddingLeft: 20, maxHeight: 120, overflowY: 'auto' }}>
              {impact.OrgNames.map(name => <li key={name}>{name}</li>)}
            </ul>
            {impact.UserCount > 0 && (
              <p style={{ fontSize: 13, color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '8px 12px', margin: '0 0 12px' }}>
                <b>{impact.UserCount} user account{impact.UserCount !== 1 ? 's' : ''}</b> under these orgs will be permanently deleted along with them.
              </p>
            )}
            {impact.DeviceCount > 0 && (
              <p style={{ fontSize: 13, color: '#1e40af', background: '#eff6ff', borderRadius: 6, padding: '8px 12px', margin: '0 0 12px' }}>
                <b>{impact.DeviceCount} device{impact.DeviceCount !== 1 ? 's' : ''}</b> still assigned somewhere in this org's subtree
                will be disabled and unassigned from their org (not deleted) — an admin can reassign and re-enable them later.
              </p>
            )}
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Type <code>{org.Name}</code> to confirm
            </label>
            <input
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginBottom: 12 }}
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              autoFocus
            />
            {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
          <button style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }} onClick={onClose}>Cancel</button>
          <button
            style={{ ...btnStyle, background: canConfirm ? '#dc2626' : '#fca5a5', color: '#fff', cursor: canConfirm ? 'pointer' : 'not-allowed' }}
            disabled={!canConfirm || deleteMut.isPending}
            onClick={() => deleteMut.mutate()}
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  )
}

// BulkDeleteOrgModal previews and confirms deleting several orgs at once.
// If a selected org is itself a descendant of another selected org, it's
// dropped from the "actually delete" set — the ancestor's cascade already
// covers it, and requesting both would just make the same org counted
// twice (or the second delete call 404 after the first already removed
// it). Impact is fetched per remaining top-level org and summed — safe
// since after that filtering none of their subtrees can overlap.
function BulkDeleteOrgModal({
  orgs, selectedIDs, onClose, onDeleted,
}: { orgs: Organization[]; selectedIDs: number[]; onClose: () => void; onDeleted: () => void }) {
  const selectedSet = new Set(selectedIDs)
  const parentOf = new Map(orgs.map(o => [o.ID, o.ParentID]))
  const isDescendantOfAnotherSelected = (id: number): boolean => {
    let cur = parentOf.get(id) ?? null
    while (cur != null) {
      if (selectedSet.has(cur)) return true
      cur = parentOf.get(cur) ?? null
    }
    return false
  }
  const topLevelIDs = selectedIDs.filter(id => !isDescendantOfAnotherSelected(id))

  const impactQueries = useQueries({
    queries: topLevelIDs.map(id => ({
      queryKey: ['org-deletion-impact', id],
      queryFn: () => getOrgDeletionImpact(id),
    })),
  })
  const isLoading = impactQueries.some(q => q.isLoading)
  const loadError = impactQueries.some(q => q.isError)
  const impacts = impactQueries.map(q => q.data).filter((d): d is OrgDeletionImpact => !!d)

  const [confirmText, setConfirmText] = useState('')
  const [results, setResults] = useState<{ name: string; error: string }[] | null>(null)

  const totalOrgs = impacts.reduce((n, i) => n + i.OrgIDs.length, 0)
  const totalUsers = impacts.reduce((n, i) => n + i.UserCount, 0)
  const totalDevices = impacts.reduce((n, i) => n + i.DeviceCount, 0)

  const deleteMut = useMutation({
    mutationFn: async () => {
      const outcomes: { name: string; error: string }[] = []
      for (const id of topLevelIDs) {
        const name = orgs.find(o => o.ID === id)?.Name ?? `#${id}`
        try {
          await deleteOrganization(id)
        } catch (e) {
          outcomes.push({ name, error: e instanceof Error ? e.message : String(e) })
        }
      }
      return outcomes
    },
    onSuccess: (outcomes) => {
      if (outcomes.length === 0) onDeleted()
      else setResults(outcomes)
    },
  })

  const confirmWord = 'DELETE'
  const canConfirm = impacts.length === topLevelIDs.length && confirmText.trim() === confirmWord

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>
          Delete {topLevelIDs.length} Organization{topLevelIDs.length !== 1 ? 's' : ''}
        </h2>

        {isLoading && <p style={{ fontSize: 13, color: '#6b7280' }}>Checking what this would affect…</p>}
        {loadError && <p style={{ color: '#dc2626', fontSize: 13 }}>Could not load deletion impact for one or more organizations.</p>}

        {!isLoading && !loadError && (
          <>
            <p style={{ fontSize: 13, color: '#374151', margin: '0 0 12px' }}>
              This deletes {totalOrgs} organization{totalOrgs !== 1 ? 's' : ''} total (including descendants):
            </p>
            <ul style={{ fontSize: 13, color: '#374151', margin: '0 0 12px', paddingLeft: 20, maxHeight: 140, overflowY: 'auto' }}>
              {impacts.flatMap(i => i.OrgNames).map((name, i) => <li key={name + i}>{name}</li>)}
            </ul>
            {totalUsers > 0 && (
              <p style={{ fontSize: 13, color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '8px 12px', margin: '0 0 12px' }}>
                <b>{totalUsers} user account{totalUsers !== 1 ? 's' : ''}</b> under these orgs will be permanently deleted along with them.
              </p>
            )}
            {totalDevices > 0 && (
              <p style={{ fontSize: 13, color: '#1e40af', background: '#eff6ff', borderRadius: 6, padding: '8px 12px', margin: '0 0 12px' }}>
                <b>{totalDevices} device{totalDevices !== 1 ? 's' : ''}</b> still assigned somewhere in these orgs' subtrees
                will be disabled and unassigned from their org (not deleted) — an admin can reassign and re-enable them later.
              </p>
            )}
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Type <code>{confirmWord}</code> to confirm
            </label>
            <input
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginBottom: 12 }}
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              autoFocus
            />
            {results && results.length > 0 && (
              <p style={{ fontSize: 13, color: '#dc2626', margin: '0 0 12px' }}>
                {results.length} failed: {results.map(r => `${r.name} (${r.error})`).join('; ')}
              </p>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
          <button style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }} onClick={onClose}>Cancel</button>
          {!isLoading && !loadError && (
            <button
              style={{ ...btnStyle, background: canConfirm ? '#dc2626' : '#fca5a5', color: '#fff', cursor: canConfirm ? 'pointer' : 'not-allowed' }}
              disabled={!canConfirm || deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete Permanently'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// PlatformLinkPanel lets an org pick one platform (any platform visible to
// the caller — the server enforces that it actually belongs to this org or
// one of its ancestors) and one reseller under it, fetched live from
// netsapiens rather than synced into a local table. A device under this
// org later picks a domain filtered to whichever reseller is set here.
function PlatformLinkPanel({ org, platforms }: { org: Organization; platforms: Platform[] }) {
  const qc = useQueryClient()
  const [platformID, setPlatformID] = useState<number | ''>(org.PlatformID ?? '')
  const [resellerID, setResellerID] = useState(org.ResellerID)
  const [resellerName, setResellerName] = useState(org.ResellerName)
  const [error, setError] = useState<string | null>(null)

  const { data: resellers = [], isFetching: fetchingResellers } = useQuery<Reseller[]>({
    queryKey: ['platform-resellers', platformID],
    queryFn: () => listPlatformResellers(platformID as number),
    enabled: platformID !== '',
  })

  // Changing the platform invalidates whatever reseller was picked under
  // the previous one.
  useEffect(() => {
    if (platformID !== org.PlatformID) {
      setResellerID('')
      setResellerName('')
    }
  }, [platformID]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveMut = useMutation({
    mutationFn: () => updateOrganizationPlatformLink(org.ID, platformID === '' ? undefined : platformID, resellerID, resellerName),
    onSuccess: () => {
      setError(null)
      qc.invalidateQueries({ queryKey: ['organizations'] })
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
  })

  return (
    <div>
      <h5 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#374151' }}>
        Platform + reseller this org is linked to (scopes the customer-domain picker when adding a device):
      </h5>
      {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 8px' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          style={inputSt}
          value={platformID}
          onChange={e => setPlatformID(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">— no platform —</option>
          {platforms.map(p => <option key={p.ID} value={p.ID}>{p.Name}</option>)}
        </select>
        {platformID !== '' && (
          <div style={{ minWidth: 220 }}>
            <SearchableSelect
              options={resellers.map(r => ({ value: r.ID, label: r.Name }))}
              value={resellerID}
              onChange={id => {
                const r = resellers.find(r => r.ID === id)
                setResellerID(r?.ID ?? '')
                setResellerName(r?.Name ?? '')
              }}
              placeholder={fetchingResellers ? 'Loading resellers…' : 'Type to search resellers…'}
              disabled={fetchingResellers}
              emptyMessage="No resellers match"
            />
          </div>
        )}
        <button
          style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
          disabled={saveMut.isPending || (platformID !== '' && resellerID === '')}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}
